import { useState, useMemo, useRef, useCallback } from 'react'
import { useFrontendLogs } from '@/hooks/useFrontendLogs'
import { useForklifts } from '@/hooks/useForklifts'
import { DEFAULT_PAGE_SIZE } from '@/config/constants'
import type { LogLevel, FrontendLog } from '@/types/frontendLog'
import { enrichErrorMessage } from '@/common/errorDescriptions'
import { getAppId } from '@/common/appLabel'

function getToday(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function useLogsPage() {
  const [page, setPage] = useState(1)
  const [levelFilter, setLevelFilter] = useState<LogLevel | ''>('')
  const [siteFilter, setSiteFilter] = useState('')
  const [forkIdFilter, setForkIdFilter] = useState('')
  const [appFilter, setAppFilter] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDate, setSelectedDate] = useState(getToday)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [selectedLog, setSelectedLog] = useState<FrontendLog | null>(null)

  const timeRange = useMemo(() => {
    const start = new Date(`${selectedDate}T00:00:00`)
    const end = new Date(`${selectedDate}T23:59:59.999`)
    return { start: start.toISOString(), end: end.toISOString() }
  }, [selectedDate])

  const { data: forkliftData } = useForklifts({ limit: 100 })
  const forkIds = useMemo(
    () => (forkliftData?.data ?? []).map((f) => f.fork_id).sort(),
    [forkliftData]
  )

  const { data, isLoading, isFetching, error } = useFrontendLogs({
    limit: 10000,
    start_time: timeRange.start,
    end_time: timeRange.end,
  })

  const siteIds = useMemo(() => {
    const allLogs = data?.data || []
    return [...new Set(allLogs.map((log) => log.site_id))]
      .filter(Boolean)
      .sort()
  }, [data])

  const filteredLogs = useMemo(() => {
    const allLogs = data?.data || []
    return allLogs.filter((log) => {
      if (siteFilter && log.site_id !== siteFilter) return false
      if (forkIdFilter && log.robot_id !== forkIdFilter) return false
      if (levelFilter && log.level !== levelFilter) return false
      if (appFilter) {
        // 'webapp' = context.app 無しの webapp 由来ログ (機体エラーは除く)
        const appId = getAppId(log)
        if (appFilter === 'webapp') {
          if (appId || log.category === 'device') return false
        } else if (appId !== appFilter) {
          return false
        }
      }
      if (searchQuery) {
        const q = searchQuery.toLowerCase()
        const enriched = enrichErrorMessage(
          log.message,
          log.context as Parameters<typeof enrichErrorMessage>[1]
        ).toLowerCase()
        const actionMatch = log.action.toLowerCase().includes(q)
        if (!enriched.includes(q) && !actionMatch) return false
      }
      return true
    })
  }, [data, siteFilter, forkIdFilter, levelFilter, appFilter, searchQuery])

  const totalFiltered = filteredLogs.length
  const totalPages = Math.ceil(totalFiltered / DEFAULT_PAGE_SIZE)

  const paginatedLogs = useMemo(
    () =>
      filteredLogs.slice(
        (page - 1) * DEFAULT_PAGE_SIZE,
        page * DEFAULT_PAGE_SIZE
      ),
    [filteredLogs, page]
  )

  const summary = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const log of filteredLogs) {
      counts[log.level] = (counts[log.level] || 0) + 1
    }
    return Object.keys(counts).length > 0 ? { level_counts: counts } : null
  }, [filteredLogs])

  const handleDateChange = useCallback((value: string) => {
    setSelectedDate(value)
    setPage(1)
  }, [])

  const handleSiteFilterChange = useCallback((value: string) => {
    setSiteFilter(value)
    setPage(1)
  }, [])

  const handleForkIdFilterChange = useCallback((value: string) => {
    setForkIdFilter(value)
    setPage(1)
  }, [])

  const handleLevelFilterChange = useCallback((value: string) => {
    setLevelFilter(value as LogLevel | '')
    setPage(1)
  }, [])

  const handleAppFilterChange = useCallback((value: string) => {
    setAppFilter(value)
    setPage(1)
  }, [])

  const handleSearchInputChange = useCallback((value: string) => {
    setSearchInput(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setSearchQuery(value)
      setPage(1)
    }, 300)
  }, [])

  // ページネーション用のページ番号リスト
  const pageNumbers = useMemo(() => {
    const pages: (number | '...')[] = []
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      pages.push(1)
      if (page > 3) pages.push('...')
      for (
        let i = Math.max(2, page - 1);
        i <= Math.min(totalPages - 1, page + 1);
        i++
      )
        pages.push(i)
      if (page < totalPages - 2) pages.push('...')
      pages.push(totalPages)
    }
    return pages
  }, [totalPages, page])

  return {
    // state
    page,
    selectedDate,
    siteFilter,
    forkIdFilter,
    levelFilter,
    appFilter,
    searchInput,
    selectedLog,
    // derived
    forkIds,
    siteIds,
    paginatedLogs,
    totalFiltered,
    totalPages,
    pageNumbers,
    summary,
    isLoading,
    isFetching,
    error,
    // actions
    setPage,
    setSelectedLog,
    handleDateChange,
    handleSiteFilterChange,
    handleForkIdFilterChange,
    handleLevelFilterChange,
    handleAppFilterChange,
    handleSearchInputChange,
  }
}
